import React, { useCallback, useState } from 'react';
import { useShowContext } from 'framework/show';
import { useAsyncValue } from 'framework/utils';
import { InputLoader } from 'framework/ui/mui-builder';
import { useArtifactService, useArtifactDownloadHelper, useArtifactPreviewHelper } from 'domain/shared/artifact';
import { BillingFormData } from '../../types';
import { Typography } from '@mui/material';

const BillingSOWFileLink: React.FC = () => {
  const { data } = useShowContext<BillingFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const billingData = data.getData();
  const { sowId, budgetId, relatedDocuments = [] } = billingData;
  const { getArtifactsDataByIds, isArtifactsInfoReceiverLoading } = useArtifactService({ budgetId });
  const relatedDocumentsKey = relatedDocuments.join(',');
  const { download } = useArtifactDownloadHelper({ budgetId });
  const { openPreview, isPreviewAvailable } = useArtifactPreviewHelper({ budgetId });

  const artifacts = useAsyncValue(async () => {
    if (isArtifactsInfoReceiverLoading || relatedDocuments.length === 0) return [];
    return getArtifactsDataByIds(relatedDocuments);
  }, [isArtifactsInfoReceiverLoading, budgetId, relatedDocumentsKey]);

  const artifact = artifacts?.[0];
  const documentId = relatedDocuments?.[0];

  const canPreview = artifact && isPreviewAvailable(artifact?.type);
  const hasPreviewCapability = canPreview && (artifact?.hasPreview || artifact?.isHasPreview);

  const handleDownload = useCallback(async () => {
    if (!documentId) return;
    download(documentId, artifact?.title);
  }, [documentId, artifact?.title, download]);

  const handleOpenPreview = useCallback(async () => {
    if (!documentId) return;
    await openPreview(documentId);
  }, [documentId, openPreview]);

  const handleClick = useCallback(async () => {
    if (!documentId) return;

    setIsLoading(true);
    try {
      if (hasPreviewCapability) {
        await handleOpenPreview();
      } else {
        await handleDownload();
      }
    } finally {
      setIsLoading(false);
    }
  }, [documentId, hasPreviewCapability, handleOpenPreview, handleDownload]);

  if (isLoading) {
    return <InputLoader />;
  }

  const linkContent = `SOW${sowId}`;

  return artifact && documentId ? (
    <Typography
      variant="h2"
      color="brand"
      component="span"
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      title={hasPreviewCapability ? 'Open preview' : 'Download file'}
    >
      {linkContent}
    </Typography>
  ) : (
    <Typography variant="h2" color="brand" component="span">
      {linkContent}
    </Typography>
  );
};

export default BillingSOWFileLink;
